/**
 * Pantalla de actualización de contraseña
 * Migrada desde la app móvil con React Query y arquitectura moderna
 */

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { FiShield, FiArrowLeft, FiEye, FiEyeOff } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useCurrentUserQuery, useUpdatePasswordMutation } from '../../hooks/useAuthMutations';
import { validatePasswordForm, getAuthErrorMessage } from '../../utils/validation';
import PasswordVerificationScreen from './PasswordVerificationScreen';

/**
 * Componente principal para actualizar la contraseña del usuario
 */
const UpdatePasswordScreen = () => {
  const navigate = useNavigate();
  
  // Estados locales
  const [newPass, setNewPass] = useState('');
  const [newRePass, setNewRePass] = useState('');
  const [showNewPass, setShowNewPass] = useState(false);
  const [showNewRePass, setShowNewRePass] = useState(false);
  const [isPasswordVerified, setIsPasswordVerified] = useState(false);

  // Hooks de TanStack Query
  const { isLoading: isLoadingUser, error: userError } = useCurrentUserQuery();
  const updatePasswordMutation = useUpdatePasswordMutation();

  // Efecto para manejar errores en la carga del usuario
  useEffect(() => {
    if (userError) {
      console.error('Error cargando usuario:', userError);
      toast.error(
        'Sesión caducada. Por seguridad cerramos la sesión.'
      );
    }
  }, [userError]);

  /**
   * Maneja la verificación exitosa de la contraseña
   */
  const handlePasswordVerified = () => {
    setIsPasswordVerified(true);
  };

  /**
   * Valida y ejecuta el cambio de contraseña
   */
  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    
    try {
      // Validar formulario
      const validation = validatePasswordForm(newPass, newRePass);
      if (!validation.isValid) {
        toast.error(validation.message);
        return;
      }

      // Actualizar contraseña
      await updatePasswordMutation.mutateAsync(newPass);

      // Mostrar mensaje de éxito
      toast.success('Contraseña actualizada.');

      // Regresar a la pantalla anterior
      navigate(-1);

    } catch (error) {
      console.error('Error actualizando contraseña:', error);
      
      const errorMessage = getAuthErrorMessage(error);
      toast.error(errorMessage);
      
      // Limpiar campos en caso de error
      setNewPass('');
      setNewRePass('');
    }
  };

  // Pantalla de carga mientras se obtiene el usuario
  if (isLoadingUser) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">Cargando...</div>
      </div>
    );
  }

  // Si no se ha verificado la contraseña, mostrar pantalla de verificación
  if (!isPasswordVerified) {
    return (
      <PasswordVerificationScreen
        title="Verificar tu contraseña"
        subtitle="Para continuar, vuelve a introducir tu contraseña de Amarsi."
        onSuccess={handlePasswordVerified}
        successMessage="Contraseña verificada correctamente"
        buttonText="Continuar"
      />
    );
  }

  // Renderizar contenido principal
  return (
    <div className="min-h-screen bg-gray-900 p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header con botón de retroceso */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white transition-colors mr-4"
            disabled={updatePasswordMutation.isLoading}
          >
            <FiArrowLeft className="h-6 w-6" />
          </button>
          <h1 className="text-2xl font-bold text-white">
            Cambiar contraseña
          </h1>
        </div>

        {/* Card principal */}
        <div className="bg-gray-800 rounded-lg shadow-xl p-6">
          {/* Header informativo */}
          <div className="flex items-start mb-6 p-4 bg-gray-700 rounded-lg">
            <FiShield className="h-6 w-6 text-violet-400 mr-3 mt-1 flex-shrink-0" />
            <div className="text-gray-300 text-sm">
              <p className="mb-2">Crea una nueva contraseña segura para tu cuenta.</p>
              <p>Usa al menos 8 caracteres para mayor seguridad.</p>
            </div>
          </div>

          {/* Formulario */}
          <form onSubmit={handleUpdatePassword} className="space-y-6">
            {/* Campo de entrada para nueva contraseña */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Contraseña nueva
              </label>
              <div className="relative">
                <input
                  type={showNewPass ? 'text' : 'password'}
                  value={newPass}
                  onChange={(e) => setNewPass(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  placeholder="Contraseña nueva"
                  autoFocus
                  disabled={updatePasswordMutation.isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPass(!showNewPass)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  disabled={updatePasswordMutation.isLoading}
                >
                  {showNewPass ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Campo de entrada para confirmar contraseña */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Confirmar la contraseña
              </label>
              <div className="relative">
                <input
                  type={showNewRePass ? 'text' : 'password'}
                  value={newRePass}
                  onChange={(e) => setNewRePass(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  placeholder="Confirmar la contraseña"
                  disabled={updatePasswordMutation.isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowNewRePass(!showNewRePass)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  disabled={updatePasswordMutation.isLoading}
                >
                  {showNewRePass ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Indicadores de validación */}
            <div className="space-y-2">
              {/* Validación de longitud mínima */}
              {newPass.length > 0 && newPass.length < 8 && (
                <p className="text-gray-400 text-sm">
                  • Al menos 8 caracteres
                </p>
              )}

              {/* Validación de coincidencia */}
              {newRePass.length > 0 && newPass !== newRePass && (
                <p className="text-red-400 text-sm">
                  • Las contraseñas no coinciden
                </p>
              )}

              {/* Indicador de éxito */}
              {newPass.length >= 8 && newPass === newRePass && newRePass.length > 0 && (
                <p className="text-green-400 text-sm">
                  ✓ Contraseña válida
                </p>
              )}
            </div>

            {/* Botón de actualización */}
            <button
              type="submit"
              disabled={
                !validatePasswordForm(newPass, newRePass).isValid || 
                updatePasswordMutation.isLoading
              }
              className="w-full bg-violet-600 hover:bg-violet-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {updatePasswordMutation.isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Procesando...
                </>
              ) : (
                'Actualizar contraseña'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdatePasswordScreen;
